/**
 * MyCardsPanel — left column of the dashboard's two-up row.
 *
 * Per Figma 562:22105: header (title + arrow link) above a list of card
 * rows; each row shows the masked name, a card-art thumbnail, spend/limit
 * amount + period label, and a thin progress bar with the limit label
 * pinned to the right.
 *
 * The art is the same CompanyPaymentCard every other surface renders, with only
 * its radius and shadow overridden for thumbnail size.
 */
import React from "react";
import { DashboardCard } from "hooks/useEmployeeDashboard";
import { mostConstrainingLimit } from "utils/cards/cardLimit";
import { LinearProgress, Stack, Typography } from "@mui/material";
import { Card } from "@outpave/ds";
import { Center, Inline } from "components/layout/reusableLayouts";
import CardType from "enums/CardType";
import CompanyPaymentCard from "components/common/cards/CompanyPaymentCard";
import RouterLinkBehavior from "components/common/RouterLinkBehavior";
import ExternalCardDesign from "assets/images/ExternalCardDesign.svg";
import { useTranslation } from "react-i18next";
import { formatCashFull } from "utils/formatCash";

/** Thumbnail width. The height follows from the face's ID-1 aspect ratio. */
const CARD_ART_WIDTH = 56;

const PROGRESS_COLOR = {
  brand: "primary",
  warning: "warning",
  danger: "error",
} as const;

/** Per-period i18n keys for the limit caption. Kept as full phrases (rather
 * than interpolating the period word) so each translates as a natural unit. */
const LIMIT_CAPTION_KEY: Record<string, string> = {
  daily: "{{amount}} daily limit",
  weekly: "{{amount}} weekly limit",
  monthly: "{{amount}} monthly limit",
  total: "{{amount}} total limit",
};

/** Title-case a card type for the fallback name, e.g. "VIRTUAL" -> "Virtual". */
function cardDisplayName(card: DashboardCard): string {
  if (card.nickname?.length) return card.nickname;
  const type = `${card.type.charAt(0).toUpperCase()}${card.type.slice(1).toLowerCase()}`;
  return `${type} Card`;
}

/** A single card row: name, art, spend/limit stat, and the limit progress bar
 * (or, for a per-transaction-only card, the per-charge cap caption). */
const CardRow: React.FC<{ card: DashboardCard; cardImage?: string }> = ({
  card,
  cardImage,
}) => {
  const { t } = useTranslation();
  const currency = card.currency ?? "USD";

  const constraining = mostConstrainingLimit(card);
  const perTransaction = constraining?.perTransaction ?? false;

  // The per-transaction cap has no cumulative usage, so its track stays empty —
  // a filled bar would chart a meaningless ratio.
  const ratio =
    constraining && !perTransaction && constraining.limit > 0
      ? constraining.spent / constraining.limit
      : 0;
  const tone = ratio > 0.9 ? "danger" : ratio > 0.7 ? "warning" : "brand";
  const pct = Math.min(100, Math.max(0, ratio * 100));

  const spent =
    constraining?.spent ?? Math.abs(card.spendingSummary?.totalSpent ?? 0);
  const periodLabel = t(constraining?.label ?? "Spent in total");
  const caption = perTransaction
    ? t("Up to {{amount}} per transaction", {
        amount: formatCashFull(constraining?.limit ?? 0, currency, false),
      })
    : constraining
      ? t(LIMIT_CAPTION_KEY[constraining.period], {
          amount: formatCashFull(constraining.limit, currency, false),
        })
      : t("No Transaction limit");

  return (
    <Stack
      sx={{
        gap: "var(--space-3)",
        padding: "var(--space-4)",
      }}
    >
      <Typography variant="h6">
        {cardDisplayName(card)}{" "}
        {card.cardNumber ? ` (...${card.cardNumber.slice(-4)})` : ""}
      </Typography>
      <Stack>
        <Inline gap="var(--space-5)">
          <CompanyPaymentCard
            width={CARD_ART_WIDTH}
            backgroundImage={cardImage}
            sx={{
              borderRadius: "var(--radius-xs)",
              boxShadow: "none",
            }}
          />
          <Stack>
            <Typography variant="stat">
              {formatCashFull(spent, currency, false)}
              {!perTransaction && (
                <>
                  /
                  {constraining
                    ? formatCashFull(constraining.limit, currency, false)
                    : "—"}
                </>
              )}
            </Typography>
            <Typography
              variant="label"
              sx={{
                color: "var(--color-text-tertiary)",
              }}
            >
              {periodLabel}
            </Typography>
          </Stack>
        </Inline>
        <Stack
          sx={{
            gap: "var(--space-2)",
            alignItems: "flex-end",
          }}
        >
          <Typography
            variant="label"
            sx={{
              color: "var(--color-text-tertiary)",
            }}
          >
            {caption}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={PROGRESS_COLOR[tone]}
            sx={{ width: "100%" }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export interface MyCardsPanelProps {
  cards: DashboardCard[];
  /** Cap the inline list. Extra cards are reachable via the arrow link. */
  maxItems?: number;
}

const MyCardsPanel: React.FC<MyCardsPanelProps> = ({ cards, maxItems = 2 }) => {
  const visible = cards.slice(0, maxItems);
  const { t } = useTranslation();

  return (
    <Card
      variant="outlined"
      title={t("My Cards")}
      subtitle={t("Manage your cards and spending at a glance")}
      action={{
        type: "link",
        href: "/my/cards",
        label: t("View All Cards"),
        component: RouterLinkBehavior,
      }}
      sx={{ flex: 1 }}
    >
      {visible.length === 0 ? (
        <Center sx={{ flex: 1, justifyContent: "center" }}>
          <Typography
            variant="body1"
            sx={{
              color: "var(--color-text-tertiary)",
              textAlign: "center",
            }}
          >
            {t("No cards assigned yet")}
          </Typography>
        </Center>
      ) : (
        <Stack
          sx={{
            gap: "var(--space-5)",
          }}
        >
          {visible.map((c: DashboardCard) => (
            <CardRow
              key={c.id}
              card={c}
              // Only the external design is this panel's to choose.
              cardImage={
                c.type === CardType.EXTERNAL ? ExternalCardDesign : undefined
              }
            />
          ))}
        </Stack>
      )}
    </Card>
  );
};

export default MyCardsPanel;
